import { useEffect, useRef, useState } from "react";
import { Bell, Loader2, FileText, Eye, X, CheckCheck } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { fmtUSDateTime } from "../lib/date";
import { toast } from "sonner";

/**
 * Bell-icon dropdown for "lender saw it" notifications.
 *
 * Polls `/pfs/notifications` every 30s while the page is open so a fresh
 * lender view shows up without the user having to refresh. Clicking a
 * notification opens its parent share-link in the audit dialog (handled
 * via `onOpenLink(linkId)` callback) and marks it read.
 *
 * Props:
 *   onOpenLink (linkId) => void
 */
export default function PfsNotificationsBell({ onOpenLink, compact = false }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/pfs/notifications");
      setItems(r.data?.items || []);
      setUnread(Number(r.data?.unread || 0));
    } catch (err) {
      // Tenants get 403; not authenticated yet → silently disable polling
      if (err.response?.status === 403 || err.response?.status === 401) {
        setItems([]);
        setUnread(0);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, []);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const markAllRead = async () => {
    try {
      await api.post("/pfs/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read_at: new Date().toISOString() })));
      setUnread(0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not mark read");
    }
  };

  const clickItem = async (n) => {
    if (!n.read_at) {
      try {
        await api.post(`/pfs/notifications/${n.id}/read`);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)));
        setUnread((u) => Math.max(0, u - 1));
      } catch { /* ignore */ }
    }
    if (n.link_id) onOpenLink?.({ linkId: n.link_id, snapshotId: n.snapshot_id });
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={
          compact
            ? "relative p-2 rounded-full hover:bg-slate-100 text-slate-700 transition-colors"
            : "relative inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-sm border border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100 transition-colors"
        }
        title="Lender activity"
        data-testid="pfs-notifications-bell"
      >
        <Bell className="w-4 h-4" />
        {!compact && "Activity"}
        {unread > 0 && (
          <span
            className={
              "absolute bg-rose-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] inline-flex items-center justify-center px-1 ring-2 ring-white "
              + (compact ? "top-0 right-0" : "-top-1.5 -right-1.5")
            }
            data-testid="pfs-notifications-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[380px] max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-sm shadow-xl z-50" data-testid="pfs-notifications-panel">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200">
            <div className="overline text-slate-700 flex items-center gap-2">
              <Bell className="w-3.5 h-3.5" /> Lender activity
            </div>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  className="text-[11px] text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 font-mono uppercase tracking-widest"
                  data-testid="pfs-notifications-read-all"
                >
                  <CheckCheck className="w-3 h-3" /> Mark all read
                </button>
              )}
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="text-slate-500 text-sm py-8 text-center inline-flex items-center justify-center gap-2 w-full">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="text-slate-500 text-sm py-10 text-center">
                <Bell className="w-6 h-6 mx-auto mb-2 text-slate-300" />
                No lender activity yet.<br />
                <span className="text-xs">Generate a share link to start tracking views.</span>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {items.map((n) => {
                  const Icon = n.action === "pdf" ? FileText : Eye;
                  const tone = n.action === "pdf" ? "text-emerald-700" : "text-slate-700";
                  const unreadBg = n.read_at ? "" : "bg-emerald-50/50";
                  return (
                    <li key={n.id} className={`px-3 py-2.5 hover:bg-slate-50 cursor-pointer ${unreadBg}`} onClick={() => clickItem(n)} data-testid={`pfs-notification-${n.id}`}>
                      <div className="flex items-start gap-2">
                        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${tone}`} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-slate-800">
                            {n.body}
                          </div>
                          <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                            {fmtUSDateTime(n.created_at)}
                            {n.ip && <span className="ml-2 text-slate-400">from {n.ip}</span>}
                          </div>
                        </div>
                        {!n.read_at && (
                          <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0 mt-2" aria-label="unread" />
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
